// Device keys: one non-extractable P-256 pair per company account.
//
// The closest the web platform gets to a hardware key: WebCrypto generates the pair
// with `extractable: false`, the CryptoKey handle is stored in IndexedDB via
// structured clone, and the private scalar can be USED to sign but never read — not
// by this code, not by an XSS payload. It is software protection, not a secure
// element; the spec requires only the curve, so this is conformant, and the honest
// difference from a phone is stated here rather than papered over.
//
// The alias scheme (`dialano_key_` + account id) matches the Android client so the
// two implementations stay mechanically comparable.

import { db } from '$lib/db/db';

import { b64uEncode } from './base64url';
import { derFromP1363 } from './der';
import { sha256 } from './hash';

export interface PublicKeyInfo {
  alg: 'ES256';
  key: string;
  id: string;
}

const ALIAS_PREFIX = 'dialano_key_';

interface CachedKey {
  privateKey: CryptoKey;
  info: PublicKeyInfo;
}

const cache = new Map<string, CachedKey>();
const inFlight = new Map<string, Promise<CachedKey>>();

async function load(accountId: string): Promise<CachedKey> {
  const cached = cache.get(accountId);
  if (cached !== undefined) {
    return cached;
  }
  let pending = inFlight.get(accountId);
  if (pending === undefined) {
    pending = loadFresh(accountId).finally(() => inFlight.delete(accountId));
    inFlight.set(accountId, pending);
  }
  return pending;
}

async function loadFresh(accountId: string): Promise<CachedKey> {
  const alias = ALIAS_PREFIX + accountId;
  const row = await db.deviceKeys.get(alias);
  if (row !== undefined) {
    const entry: CachedKey = {
      privateKey: row.privateKey,
      info: { alg: 'ES256', key: row.publicKeyB64, id: row.keyId },
    };
    cache.set(accountId, entry);
    return entry;
  }

  const pair = (await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false, // the private key is never extractable
    ['sign'],
  )) as CryptoKeyPair;
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey));
  const publicKeyB64 = b64uEncode(raw);
  const keyId = b64uEncode(await sha256(raw));
  await db.deviceKeys.put({
    alias,
    privateKey: pair.privateKey,
    publicKeyB64,
    keyId,
    createdAtMs: Date.now(),
  });
  const entry: CachedKey = {
    privateKey: pair.privateKey,
    info: { alg: 'ES256', key: publicKeyB64, id: keyId },
  };
  cache.set(accountId, entry);
  return entry;
}

/** The public key for [accountId], creating the pair on first use. */
export async function keyFor(accountId: string): Promise<PublicKeyInfo> {
  return (await load(accountId)).info;
}

/** DER-encoded ECDSA signature over [data] with the account's device key. */
export async function signWithDeviceKey(accountId: string, data: Uint8Array): Promise<Uint8Array> {
  const { privateKey } = await load(accountId);
  const rs = new Uint8Array(
    await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, privateKey, data as BufferSource),
  );
  return derFromP1363(rs);
}

/** Removes the account's key — called from contact deletion. */
export async function deleteDeviceKey(accountId: string): Promise<void> {
  cache.delete(accountId);
  await db.deviceKeys.delete(ALIAS_PREFIX + accountId);
}

// ——————————————————————————————————————————————————————————————
// The device↔directory key
// ——————————————————————————————————————————————————————————————

/** Reserved pseudo-account for the directory. Company account ids never carry
 *  underscores in this deployment, and the double underscore keeps it out of any
 *  plausible id space regardless. */
const DIRECTORY_ACCOUNT = '__directory__';

/** ONE key for the directory, deliberately — unlike the per-company keys above.
 *  Per-company keys exist so companies cannot recognise the same buyer by comparing
 *  notes; the directory is a single counterparty, so there is nobody to compare notes
 *  WITH, and the per-key rate budget of the entry gate only works if this client is
 *  one bucket rather than a fresh one per ask. Same mechanism, same protection:
 *  WebCrypto, `extractable: false` — software protection, not a secure element. */
export async function directoryKey(): Promise<PublicKeyInfo> {
  return keyFor(DIRECTORY_ACCOUNT);
}

/** DER-encoded ECDSA signature over [data] with the device↔directory key. */
export async function signWithDirectoryKey(data: Uint8Array): Promise<Uint8Array> {
  return signWithDeviceKey(DIRECTORY_ACCOUNT, data);
}
